import { useEffect, useState } from "react";
import { Smartphone, Share, SquarePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("vr-can-install"));
  });
}

export const InstallAppCard = () => {
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("vr-install-dismissed") === "1");
  const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);
  const [iosOpen, setIosOpen] = useState(false);
  useEffect(() => {
    const on = () => setCanPrompt(true);
    const installed = () => {
      localStorage.setItem("vr-install-dismissed", "1");
      setDismissed(true);
      toast.success("VoltRoute staat nu als app op je toestel");
    };
    window.addEventListener("vr-can-install", on);
    window.addEventListener("appinstalled", installed);
    return () => { window.removeEventListener("vr-can-install", on); window.removeEventListener("appinstalled", installed); };
  }, []);
  if (isStandalone || dismissed || (!canPrompt && !isIos)) return null;

  const install = async () => {
    if (!deferredPrompt) { setIosOpen(true); return; }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanPrompt(false);
    if (outcome !== "accepted") setCanPrompt(true);
  };
  const dismiss = () => { localStorage.setItem("vr-install-dismissed", "1"); setDismissed(true); };

  return (
    <>
      <div data-testid="install-app-card" className="bg-card border border-border rounded-2xl p-4 sm:p-5 mb-8 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-primary" />
        </span>
        <p className="text-sm text-muted-foreground flex-1 min-w-0">
          <strong className="text-foreground">Zet VoltRoute op je beginscherm</strong> — sneller starten, volledig scherm en je trip ook offline bij de hand.
        </p>
        <button data-testid="install-app-button" onClick={install}
          className="shrink-0 rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2.5 text-xs font-bold active:scale-95 transition-[transform,background-color] duration-200">
          Installeer de app
        </button>
        <button data-testid="install-app-dismiss" onClick={dismiss} title="Niet meer tonen"
          className="shrink-0 p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
          <X className="w-4 h-4" />
        </button>
      </div>
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="ios-install-dialog" className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Zet VoltRoute op je beginscherm</DialogTitle>
            <DialogDescription>Zo installeer je de app op je iPhone of iPad:</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">1</span>
              <span className="flex items-center gap-1.5 flex-wrap">Tik in Safari op de <Share className="w-4 h-4 text-primary inline" /> <strong>deel-knop</strong> (onderaan)</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">2</span>
              <span className="flex items-center gap-1.5 flex-wrap">Kies <SquarePlus className="w-4 h-4 text-primary inline" /> <strong>"Zet op beginscherm"</strong></span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">3</span>
              <span>Tik rechtsboven op <strong>"Voeg toe"</strong> — klaar!</span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
};
